import axios from 'axios'
import Api from 'clients/web/Api'
import WebRepository from 'clients/web/WebRepository'
import Allergy from 'model/Allergy'
import { ExamResultStatus } from 'model/Examination'
import InternalExaminationPlan from 'model/InternalExaminationPlan'
import PatientFile from 'model/PatientFile'
import PatientsDiagnosis from 'model/PatientsDiagnosis'
import PatientsDiagnosisMedications from 'model/PatientsDiagnosisMedications'
import PatientsDiagnosisSimple from 'model/PatientsDiagnosisSimple'
import PatientsDiagnosisTopics from 'model/PatientsDiagnosisTopics'
import PatientDiagnosisDiscussions from '../../model/PatientsDiagnosisDiscussions'

const { server } = (window as any).config

export type TestOrderNote = {
  patientId: string
  tests: { name: string; reasoning?: string }[]
  // The doctor a nurse ordered on behalf of; null when a doctor ordered directly.
  prescribedBy?: string | null
  patientDiagnoses?: { diagnosisCode: string; diagnosisId?: string; diagnosisName?: string }[]
}

// The bare-axios calls below send the session cookie only if something has set
// this, and this module never did — it worked because ~20 other API modules set
// the same global at import time and one of them was always loaded first.
//
// That held for every screen behind the login and broke on the first one that is
// not: the public result viewer's chunk pulls in this repository to order an AI
// recommendation without pulling in any of those modules, so the cookie was
// dropped and the server answered 401 to a signed-in doctor. Set here so the
// module no longer depends on who else happens to have been imported.
axios.defaults.withCredentials = true

export class PatientDiagnosisRepository extends WebRepository<
  PatientsDiagnosis | PatientsDiagnosisTopics
> {
  constructor() {
    super(new Api('patients_diagnosis'))
  }

  async findAllPatientTopics(patientId: object): Promise<PatientsDiagnosisTopics[]> {
    const result = await axios.get(server + '/patients_diagnosis/findAllPatientsTopics', {
      params: patientId,
    })
    return result.data?.patientTopics
  }

  async findAllPatientDiagnosis(patientId: object): Promise<PatientsDiagnosis[]> {
    const result = await axios.get(server + '/patients_diagnosis/findAllPatientDiagnosis', {
      params: patientId,
    })
    return result.data?.patientDiagnosis
  }

  async findAllPatientMedications(patientId: object): Promise<PatientsDiagnosisMedications[]> {
    const result = await axios.get(server + '/patients_diagnosis/findAllPatientMedications', {
      params: patientId,
    })
    return result.data?.patientMedications
  }

  async findAllPatientAllergies(patientId: object): Promise<Allergy[]> {
    const result = await axios.get(server + '/patients_diagnosis/findAllPatientAllergies', {
      params: patientId,
    })
    return result.data?.patientAllergies
  }

  /**
   * One page of examinations, newest first, without the device payloads — on a real patient those
   * were 42.5MB of a 42.6MB response. Use `findPatientExamination` when opening a result.
   *
   * `status` narrows the page to one workspace tab, sorted server-side by the same rules as the
   * `buckets` memo in `VdgExaminationResults`. With it, `totalCount` is that tab's total and
   * `statusCounts` carries all three tabs' counts for the whole patient.
   */
  async findAllPatientExaminations(params: {
    patientId: string
    bookmark?: number
    limit?: number
    status?: ExamResultStatus
  }): Promise<{
    examinations: any[]
    bookmark: number
    hasMore: boolean
    totalCount: number
    statusCounts?: Record<ExamResultStatus, number>
  }> {
    const result = await axios.get(server + '/patients_diagnosis/findAllPatientExaminations', {
      params,
    })

    const examinations = result.data?.patientExaminations ?? []

    return {
      examinations,
      bookmark: result.data?.bookmark ?? (params.bookmark ?? 0) + 1,
      // Server-authoritative where available; the derived fallback keeps a frontend running ahead
      // of the server on the old behaviour rather than on a list that cannot load.
      hasMore: result.data?.hasMore ?? (!!params.limit && examinations.length >= params.limit),
      totalCount: result.data?.totalCount ?? examinations.length,
      statusCounts: result.data?.statusCounts,
    }
  }

  /**
   * One examination with its result document merged in, blobs included — for the result modal.
   *
   * `patientId` is required: the server matches on it alongside the id so that knowing an ObjectId
   * is not on its own enough to read a result document.
   */
  async findPatientExamination(id: string, patientId: string): Promise<any> {
    const result = await axios.get(server + `/patients_diagnosis/patientExamination/${id}`, {
      params: { patientId },
    })
    return result.data?.patientExamination
  }

  async findAllPatientSchedulePlans(patientId: object): Promise<InternalExaminationPlan[]> {
    const result = await axios.get(server + '/patients_diagnosis/findAllPatientSchedulePlans', {
      params: patientId,
    })
    return result.data?.schedulePlans
  }

  /**
   * One page of follow-ups, newest first. Omitting `limit` returns everything, so callers that
   * cannot page are unaffected.
   *
   * `reviewFilter` narrows the page to unreviewed follow-ups server-side. Filtering the loaded rows
   * instead meant the Unreviewed tab showed however many of the current page happened to be
   * unreviewed, while its badge showed the patient's real count — three in the badge, one in the
   * list. The counts below are unaffected by it: both are counted over the whole patient, so both
   * tab badges stay right whichever tab is active.
   */
  async findAllPatientDiscussions(params: {
    patientId: string
    bookmark?: number
    limit?: number
    reviewFilter?: 'unreviewed'
  }): Promise<{
    discussions: PatientDiagnosisDiscussions[]
    bookmark: number
    hasMore: boolean
    unreviewedCount: number
    totalCount: number
  }> {
    const result = await axios.get(server + '/patients_diagnosis/findAllPatientDiscussions', {
      params,
    })

    const discussions = result.data?.patientDiscussions ?? []

    return {
      discussions,
      bookmark: result.data?.bookmark ?? (params.bookmark ?? 0) + 1,
      // Server-authoritative where available. The derived fallback — a full page means another may
      // exist — costs one dead click at exhaustion, and is kept only so a frontend running ahead of
      // the server degrades to the old behaviour rather than to a list that cannot load.
      hasMore: result.data?.hasMore ?? (!!params.limit && discussions.length >= params.limit),
      // Counted server-side across every page, so the leave-prompt and "(n)" heading stay correct
      // for follow-ups the user has not scrolled far enough to load.
      unreviewedCount: result.data?.unreviewedCount ?? 0,
      totalCount: result.data?.totalCount ?? discussions.length,
    }
  }

  async findAllPatientMedicalReferralResults(patientId: object): Promise<PatientFile[]> {
    const result = await axios.get(
      server + '/patients_diagnosis/findAllPatientMedicalReferralResults',
      {
        params: patientId,
      },
    )
    return result.data?.patientMedicalReferralResults
  }

  async getAppointmentOwner(patientId: object) {
    const result = await axios.get(server + '/patients_diagnosis/getAppointmentOwner', {
      params: patientId,
    })
    return result.data
  }

  async setAppointmentOwner(patientId: string, userId: string | null) {
    const result = await axios.get(server + '/patients_diagnosis/setAppointmentOwner', {
      params: { patientId, userId },
    })
    return result.data
  }

  async createTopic(
    topic: PatientsDiagnosisTopics,
    firstDiagnosis: PatientsDiagnosis,
  ): Promise<PatientsDiagnosisTopics> {
    const newTopic = await axios.post(server + '/patients_diagnosis/createTopic', {
      topic,
      firstDiagnosis,
    })
    return newTopic.data
  }

  async addDiagnosisToTopic(
    topicId: string,
    diagnosis: PatientsDiagnosis,
  ): Promise<PatientsDiagnosisTopics> {
    const updatedTopic = await axios.post(server + '/patients_diagnosis/addDiagnosisToTopic', {
      topicId,
      diagnosis,
    })
    return updatedTopic.data
  }

  async mergeTopics(
    topicId: string,
    diagnosis: PatientsDiagnosis,
  ): Promise<PatientsDiagnosisTopics> {
    const updatedTopic = await axios.post(server + '/patients_diagnosis/mergeTopics', {
      topicId,
      diagnosis,
    })
    return updatedTopic.data
  }

  async deleteTopic(topicId: string): Promise<PatientsDiagnosisTopics> {
    const updatedTopic = await axios.post(server + '/patients_diagnosis/deleteTopic', {
      topicId,
    })
    return updatedTopic.data
  }

  async updateTopicStatus(topicId: string, status: string): Promise<PatientsDiagnosisTopics> {
    const updatedStatus = await axios.post(server + '/patients_diagnosis/updateTopicStatus', {
      topicId,
      status,
    })
    return updatedStatus.data
  }

  async addDiagnosis(diagnosis: PatientsDiagnosis): Promise<PatientsDiagnosis> {
    const newDiagnosis = await axios.post(server + '/patients_diagnosis/createNewDiagnosis', {
      diagnosis,
    })
    return newDiagnosis.data
  }

  async updateDiagnosisInTopic(
    topicId: string,
    diagnosisToUpdate: PatientsDiagnosisSimple,
  ): Promise<PatientsDiagnosisTopics> {
    const updatedTopic = await axios.post(server + '/patients_diagnosis/updateDiagnosisInTopic', {
      topicId,
      diagnosisToUpdate,
    })
    return updatedTopic.data
  }

  async getMedicationHistory(patientId: any, medicationId: any): Promise<any[]> {
    const result = await axios.get(server + '/patients_diagnosis/getMedicationHistory', {
      params: { patientId, medicationId },
    })

    return result.data?.medicationHistory
  }

  async editMedicationHistory(patientId: any, medicationId: any, history: any) {
    await axios.post(server + '/patients_diagnosis/editMedicationHistory', {
      patientId,
      medicationId,
      history,
    })
  }

  async addDiscussionToTopic(
    discussionToAdd: PatientDiagnosisDiscussions,
  ): Promise<PatientDiagnosisDiscussions> {
    const result = await axios.post(server + '/patients_diagnosis/addDiscussionToTopic', {
      discussionToAdd,
    })

    return result.data?.newDiscussion
  }

  async updateDiscussionInTopic(
    discussionToUpdate: PatientDiagnosisDiscussions,
  ): Promise<PatientsDiagnosisTopics> {
    const updatedDiscussion = await axios.post(
      server + '/patients_diagnosis/updateDiscussionInTopic',
      {
        discussionToUpdate,
      },
    )
    return updatedDiscussion.data.updatedDiscussion
  }

  async addMedicationToTopic(medicationToAdd: PatientsDiagnosisMedications) {
    const result = await axios.post(server + '/patients_diagnosis/addMedicationToTopic', {
      medicationToAdd,
    })

    return result.data?.newMedication
  }

  async updateMedication(medicationToUpdate: PatientsDiagnosisMedications) {
    const result = await axios.post(server + '/patients_diagnosis/updateMedication', {
      medicationToUpdate,
    })

    return result.data?.updatedMedication
  }

  async addAllergy(allergyToAdd: Allergy) {
    const result = await axios.post(server + '/patients_diagnosis/addAllergy', {
      allergyToAdd,
    })

    return result.data?.newAllergy
  }

  async updateAllergy(allergyToUpdate: Allergy) {
    const result = await axios.post(server + '/patients_diagnosis/updateAllergy', {
      allergyToUpdate,
    })

    return result.data?.updatedAllergy
  }

  async createInternalExaminationPlan(examPlan: InternalExaminationPlan) {
    const result = await axios.post(server + '/patients_diagnosis/createInternalExaminationPlan', {
      examPlan,
    })

    return result
  }

  /**
   * One progress note (synced to PCC) for recommended tests just ordered together, each with its
   * reason. Send only the tests whose order went through.
   */
  async addTestOrderNote(note: TestOrderNote) {
    const result = await axios.post(server + '/patients_diagnosis/addTestOrderNote', note)

    return result.data?.newDiscussion
  }

  /** Closes the plan (cancelling its future, not-performed tests) and creates the edited one. */
  async editInternalExaminationPlan(examPlanId: string, examPlan: InternalExaminationPlan) {
    const result = await axios.post(server + '/patients_diagnosis/editInternalExaminationPlan', {
      examPlanId,
      examPlan,
    })

    return result
  }

  async closeInternalExaminationPlan(examPlanId: string, discussion?: string) {
    const result = await axios.post(server + '/patients_diagnosis/closeInternalExaminationPlan', {
      examPlanId,
      discussion,
    })

    return result
  }

  async checkIfScheduleExists(examPlan: any) {
    const result = await axios.post(server + '/patients_diagnosis/checkIfScheduleExists', {
      examPlan,
    })

    return result
  }

  async signMonitoringAsViewed(patientId: any) {
    const result = await axios.post(server + '/patients_diagnosis/signMonitoringAsViewed', {
      patientId,
    })

    return result
  }

  async signLabsAsViewed(patientId: any) {
    const result = await axios.post(server + '/patients_diagnosis/signLabsAsViewed', {
      patientId,
    })

    return result
  }

  async sendDiagnosisToSpecialist(patientId: string, speciality: string, diagnosisCode: string) {
    const result = await axios.post(server + '/patients_diagnosis/sendDiagnosisToSpecialist', {
      patientId,
      speciality,
      diagnosisCode,
    })

    return result
  }
}

export default new PatientDiagnosisRepository()
